/**
 * React Hook for Access Control
 * 
 * Provides easy access to the centralized access control system
 * within React components.
 */

import { useUser } from '../context/UserContext';
import { 
  hasPageAccess, 
  hasAgentAccess, 
  hasAgencyAdminAccess, 
  hasParkProAdminAccess,
  getAccessiblePages,
  getPageAccessInfo,
  PAGE_ACCESS
} from '../utils/accessControl';

/**
 * Main access control hook
 * @returns {Object} Access control functions and user info
 */
export const useAccessControl = () => {
  const { user } = useUser();
  
  return {
    user,
    // Basic access checks
    hasAgentAccess: () => hasAgentAccess(user),
    hasAgencyAdminAccess: () => hasAgencyAdminAccess(user),
    hasParkProAdminAccess: () => hasParkProAdminAccess(user),
    
    // Page-specific access checks
    hasPageAccess: (pageKey) => hasPageAccess(user, pageKey),
    
    // Utility functions
    getAccessiblePages: () => getAccessiblePages(user),
    getPageAccessInfo: (pageKey) => getPageAccessInfo(user, pageKey),
    
    // Legacy compatibility
    hasCRMAccess: () => hasAgentAccess(user),
    hasNewDestinationAccess: () => hasParkProAdminAccess(user)
  };
};

/**
 * Hook for specific page access
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {Object} Page access info
 */
export const usePageAccess = (pageKey) => {
  const { user } = useUser();
  
  return {
    hasAccess: hasPageAccess(user, pageKey),
    accessInfo: getPageAccessInfo(user, pageKey),
    user
  };
};

/**
 * Hook for role-based access
 * @param {string} requiredRole - Required role level
 * @returns {Object} Role access info
 */
export const useRoleAccess = (requiredRole) => {
  const { user } = useUser();
  
  const hasAccess = user && user.role === requiredRole;
  
  return {
    hasAccess,
    user,
    userRole: user?.role,
    requiredRole
  };
};

/**
 * Hook for conditional rendering based on access
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {boolean} Whether to render the component
 */
export const useConditionalAccess = (pageKey) => {
  const { user } = useUser();
  return hasPageAccess(user, pageKey);
};
